import type {
  MandatoryTreeMember,
} from './MandatoryTreeMutationAuthority';

import type {
  TreeMutationProposal,
} from './TreeMutationProposalBuilder';

export interface InsertProposalInput {
  id: string;
  row: Record<string, unknown>;
  fallbackParentIds?: string[];
  fallbackSpouseId?: string | null;
}

const STRUCTURAL_DATABASE_FIELDS = new Set([
  'id',
  'parent_ids',
  'spouse_id',
  'claimed_by_user_id',
  'deleted_at',
]);

function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  return [
    ...new Set(
      value.filter(
        (item): item is string =>
          typeof item === 'string' &&
          item.length > 0,
      ),
    ),
  ];
}

function nullableString(
  value: unknown,
): string | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return typeof value === 'string'
    ? value
    : undefined;
}

export function mapDatabaseRowToMandatoryMember(
  id: string,
  row: Record<string, unknown>,
  fallbacks?: {
    parentIds?: string[];
    spouseId?: string | null;
  },
): MandatoryTreeMember {
  const deletedAt = row.deleted_at;

  return {
    id,
    parentIds:
      row.parent_ids === undefined
        ? [...(fallbacks?.parentIds ?? [])]
        : stringArray(row.parent_ids),
    spouseId:
      row.spouse_id === undefined
        ? fallbacks?.spouseId ?? null
        : nullableString(row.spouse_id) ?? null,
    claimedByUserId:
      nullableString(row.claimed_by_user_id) ??
      null,
    profileState:
      deletedAt
        ? 'deleted'
        : nullableString(row.profile_state) ??
          'active',
    approvalStatus:
      nullableString(row.approval_status) ??
      null,
  };
}

export function buildInsertProposal(
  input: InsertProposalInput,
): TreeMutationProposal {
  return {
    kind: 'insert-member',
    member: mapDatabaseRowToMandatoryMember(
      input.id,
      input.row,
      {
        parentIds:
          input.fallbackParentIds ?? [],
        spouseId:
          input.fallbackSpouseId ?? null,
      },
    ),
  };
}

export function buildGenericUpdateProposal(
  memberId: string,
  row: Record<string, unknown>,
): TreeMutationProposal {
  for (const field of Object.keys(row)) {
    if (STRUCTURAL_DATABASE_FIELDS.has(field)) {
      throw new Error(
        `generic-update-structural-field:${field}`,
      );
    }
  }

  const patch: Partial<MandatoryTreeMember> = {};

  if (row.profile_state !== undefined) {
    patch.profileState =
      nullableString(row.profile_state) ?? null;
  }

  if (row.approval_status !== undefined) {
    patch.approvalStatus =
      nullableString(row.approval_status) ?? null;
  }

  return {
    kind: 'update-member',
    memberId,
    patch,
  };
}

export interface RestoreSnapshotEvidence {
  member: MandatoryTreeMember;
  childIds: string[];
  spouseReferenceIds: string[];
}

export function buildRestoreProposal(
  evidence: RestoreSnapshotEvidence,
): TreeMutationProposal {
  return {
    kind: 'restore-member',
    member: {
      ...evidence.member,
      parentIds: [
        ...(evidence.member.parentIds ?? []),
      ],
      profileState:
        evidence.member.profileState === 'deleted'
          ? 'placeholder'
          : evidence.member.profileState,
    },
    childIds: [...evidence.childIds],
    spouseReferenceIds: [
      ...evidence.spouseReferenceIds,
    ],
  };
}
